import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MapPin, ChevronRight, X, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Sticky banner highlighting bookings whose pickup/return geometry is risky:
 * - Pickup more than 50 km from base
 * - Next-booking unreachable in available window
 * - Return location far from pickup
 *
 * Sits below the operational WarningBanner so staff see geographic risks
 * first thing on the dashboard. Auto-hides when nothing is critical/high.
 */
export default function LocationWarningsBanner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/bookings/location-risks/summary")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    // Re-check every 5 minutes while dashboard is open
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  if (loading || !data) return null;
  const total = data.total || 0;
  if (total === 0) return null;

  const critical = data.count_critical || 0;
  const tone = critical > 0 ? "rose" : "amber";

  const palette = tone === "rose"
    ? { border: "border-rose-200", bg: "bg-rose-50", text: "text-rose-900", bar: "bg-rose-500", chip: "bg-rose-100 text-rose-900 border-rose-200" }
    : { border: "border-amber-200", bg: "bg-amber-50", text: "text-amber-900", bar: "bg-amber-500", chip: "bg-amber-100 text-amber-900 border-amber-200" };

  const label = total === 1 ? "1 location risk" : `${total} location risks`;

  if (collapsed) {
    return (
      <button
        data-testid="location-warning-banner-collapsed"
        onClick={() => setCollapsed(false)}
        className={`mb-4 w-full text-left rounded-lg border ${palette.border} ${palette.bg} px-4 py-2 flex items-center justify-between hover:brightness-95 transition`}
      >
        <span className={`inline-flex items-center gap-2 text-sm font-medium ${palette.text}`}>
          <MapPin className="w-4 h-4" />
          {label} on upcoming bookings
        </span>
        <span className="text-xs text-slate-600 underline">Expand</span>
      </button>
    );
  }

  return (
    <div
      data-testid="location-warning-banner"
      className={`mb-6 rounded-xl border ${palette.border} ${palette.bg} overflow-hidden shadow-sm`}
    >
      <div className={`h-1 ${palette.bar}`} />
      <div className="p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-start">
        <div className={`w-9 h-9 rounded-lg grid place-items-center bg-white border ${palette.chip} flex-shrink-0`}>
          {critical > 0 ? <AlertTriangle className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${palette.text}`}>
              {critical > 0 ? "Critical · Location risk" : "Location attention"}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${palette.chip}`}>{label}</span>
          </div>
          <ul className="mt-2 space-y-1.5">
            {(data.items || []).slice(0, 3).map((it, idx) => (
              <li key={`${it.booking_id}-${idx}`} className="text-sm" data-testid={`location-warning-item-${idx}`}>
                <Link
                  to={`/app/bookings/${it.booking_id}`}
                  className={`hover:underline font-medium ${palette.text}`}
                >
                  {it.booking_reference || it.customer_name || "Booking"}
                </Link>
                <span className="text-slate-600">: {it.message}</span>
              </li>
            ))}
            {data.items?.length > 3 && (
              <li className="text-xs text-slate-500">+ {data.items.length - 3} more upcoming…</li>
            )}
          </ul>
        </div>
        <div className="flex items-center gap-2 self-start">
          <Link
            to="/app/bookings"
            data-testid="location-warning-open-bookings"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-sm font-medium text-slate-800"
          >
            Review bookings <ChevronRight className="w-3.5 h-3.5" />
          </Link>
          <button
            data-testid="location-warning-collapse"
            onClick={() => setCollapsed(true)}
            className="p-1.5 rounded-md hover:bg-white text-slate-600"
            title="Minimise"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
